import { useDispatch, useSelector } from 'react-redux';
import React, {
  ChangeEventHandler,
  CSSProperties,
  forwardRef,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { menuActions } from '../../state/modules/menu';
import styles from '../../styles/partials/_navmenu.css';
import DraggableMenu from '../../components/DraggableMenu';
import { convertModuleName } from '../../utils/getModuleName';
import getIn from '../../components/connectedForm/utils/getIn';
import { SystemFolder } from '../../graphql/entities';
import normalizeVariables from '../../graphql/schema/normalizeVariables';
import CompanySetupMenu from '../../views/company/CompanySetupMenu';
import getValue from '../connectedForm/utils/getValue';
import styled from 'styled-components';
// @ts-ignore
import { Analytics } from 'mixpanel';
import getClientMutationId from '../../utils/getClientMutationId';
import { getMenuIcon } from '../MenuOptions/MenuOptionIcons';
import useMenuOptions from '../MenuOptions/useMenuOptions';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import { useCookies } from 'react-cookie';
import { useHistory } from 'react-router';
import { Tooltip } from '@fluentui/react-components';
import { IReduxStore } from '../../@types/reduxTypes';
import {
  IFolder,
  IFolderInput,
  IFullUserDetails,
  IScreen,
  ISystemCompany,
  ISystemFolderMutationResult,
  ISystemFolderMutationVariables,
  ISystemMenuListQueryResult,
  ISystemMenuListQueryVariables,
  ISystemFolderAndScreenMutationResult,
  ISystemFolderAndScreenMutationVariables,
  Maybe,
} from '../../graphql/schema/p4types';
import { IAppMenu, IMenuEntry } from '../../@types/menuTypes';
import { useMutation, useQuery } from '@apollo/client';

const { update, menuList } = SystemFolder;

const MENU_SECTION = 'menuSection';

const SearchContainer = styled.div`
  display: block;
  width: 100%;
`;

const SearchInput = styled.input`
  display: inline-block;
  margin: 10px 10px 6px 9px;
  border-radius: 4px;
  border: 1px solid #eee;
  background-color: ${p4Tokens.colorNeutralBackground1};
  width: 230px;
  padding: 8px;
  font-size: 14px;
  data-lpignore: true; //Prevent lastpass from erroneously tagging against this field.
`;

const ClearSearch = styled.div`
  cursor: pointer;
  position: absolute;
  right: 15px;
  top: 18px;
  color: #607d88;
`;

const NavMenuItem = ({
  href,
  tabIndex,
  title,
  prefix,
  onClick,
  className,
}: {
  href?: string;
  tabIndex: number;
  title: string;
  prefix: ReactElement | string;
  onClick?: MouseEventHandler;
  className?: string;
}) => {
  const content = (
    <a
      href={href}
      tabIndex={tabIndex}
      onClick={(e) => {
        e.preventDefault();
        onClick?.(e);
      }}
      className={className}
    >
      {prefix}
      <div className="navmenu-title">{convertModuleName(title)}</div>
    </a>
  );

  return (
    <Tooltip
      relationship={'label'}
      content={title}
      positioning={'after'}
      showDelay={title && title.length > 15 ? 0 : 1500}
    >
      {content}
    </Tooltip>
  );
};

type INavMenuProps = {
  company: Maybe<ISystemCompany>;
  location: any;
  focusArea: (area: string, forceFocus: boolean) => void;
  clearPreviousLocation: () => void;
  tabIndex: number;
  onMenuClick: (menuState: any) => void;
  currentUser: Maybe<IFullUserDetails>;
};

const NavMenu = forwardRef<any, INavMenuProps>(
  ({ company, location, focusArea, clearPreviousLocation, tabIndex, onMenuClick, currentUser }, ref) => {
    const appMenu = useSelector((store: IReduxStore) => store.app?.menu);
    const customMenu = useSelector((store: IReduxStore) => store.menu);
    const appMenuHidden = useSelector((store: IReduxStore) => store.app.appMenuHidden);

    const menuState = !appMenuHidden;

    const [menu, setMenu] = useState<string | undefined>();
    const [menuOption, setMenuOption] = useState(1);
    const [currentFolder, setCurrentFolder] = useState<number | null>(null);
    const [editNames, setEditNames] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const menuOptions = useMenuOptions(currentFolder);

    const [cookies, setCookies] = useCookies([MENU_SECTION]);

    const loadMenuRef = useRef(true);
    const menuHeightRef = useRef(window.innerHeight > 960 ? '85%' : '80%');
    const fullMenuRef = useRef<Array<any>>([]);
    const favMenuRef = useRef<Array<IMenuEntry> | undefined>();

    const dispatch = useDispatch();
    const history = useHistory();

    const [folderListUpdate] = useMutation<
      ISystemFolderAndScreenMutationResult,
      ISystemFolderAndScreenMutationVariables
    >(SystemFolder.multiUpdate);

    const [folderUpdate] = useMutation<ISystemFolderMutationResult, ISystemFolderMutationVariables>(update);

    const { data: systemMenuData, loading: systemMenuLoading } = useQuery<
      ISystemMenuListQueryResult,
      ISystemMenuListQueryVariables
    >(menuList);

    const favouritesData = useMemo(() => {
      if (systemMenuLoading || !systemMenuData?.folders || !systemMenuData?.screens) {
        return {};
      }

      const folders = systemMenuData.folders.edges?.map((edge) => edge?.node as IFolder) ?? [];
      const screens = systemMenuData.screens.edges?.map((edge) => edge?.node as IScreen) ?? [];

      return { folders, screens };
    }, [systemMenuLoading, systemMenuData?.folders, systemMenuData?.screens]);

    //didMount
    useEffect(() => {
      let currentMenuSection = parseInt(cookies[MENU_SECTION], 10);

      if (!company) {
        selectMenu(3);
      } else {
        if (currentMenuSection === 3) {
          currentMenuSection = 2;
        }

        if (currentMenuSection) {
          selectMenu(currentMenuSection);
        }
      }
    }, []);

    useEffect(() => {
      if (favouritesData && favouritesData.folders && loadMenuRef.current) {
        dispatch(menuActions.serviceListToFolder(favouritesData));
        dispatch(menuActions.serviceListToMenu(favouritesData));
        loadMenuRef.current = false;
      }
    }, [favouritesData, dispatch]);

    useEffect(() => {
      Analytics.track('Page view');
    }, [location?.pathname]);

    const selectMenu = (menuOption: number, searchValue = '') => {
      if (menuOption > 0) {
        setCookies(MENU_SECTION, menuOption, { path: '/' });
      }
      let selectedMenuOption: number = menuOption;
      if (searchValue === '' && menuOption === 0) {
        selectedMenuOption = parseInt(cookies[MENU_SECTION], 10);
      }
      setMenuOption(selectedMenuOption);
      setSearchValue(searchValue);
    };

    const setFolder = (folderId: number) => {
      setCurrentFolder(folderId);
    };

    const onFolderChange = (id: number, value: string) => {
      const requestBody = {
        id: id,
        name: value,
      };
      dispatch(menuActions.updateFolderName(requestBody));
    };

    const onFolderBlur = (concurrencyVersion: number, id: number, value: string) => {
      folderUpdate({
        variables: {
          input: {
            clientMutationId: getClientMutationId(),
            folder: {
              name: value,
              id: id,
              concurrencyVersion: concurrencyVersion,
            },
          },
        },
      }).then((_data) => {
        if (_data.data?.folderUpdate?.result) {
          dispatch(menuActions.serviceDataToFolder(_data.data.folderUpdate.result));
        }
      });
    };

    const toggleEdit = () => {
      setEditNames((oldEditNames) => !oldEditNames);
    };

    const focusMenuArea = () => {
      focusArea('Menu', false);
    };

    const changeMenu = (menuOption: string, changeMenu: boolean | string = false) => {
      clearPreviousLocation?.();
      if (changeMenu) {
        let path = `/menu/${menuOption}`;
        if (changeMenu === 'screen') {
          path = menuOption;
        }
        history.push({
          pathname: path,
          state: {
            previousLocation: history.location?.pathname || 'back',
          },
        });
      }
      setMenu(menuOption);
    };

    const createFilteredMenu = () => {
      const sidebarOptions: Array<ReactElement> = [];
      let menuIcon: ReactElement | string = '';

      menuOptions.resetMenuOptions(appMenu); //Reinitializes the list as unfiltered.
      menuOptions.searchOptions(searchValue).forEach((option, index) => {
        menuIcon = getMenuIcon(option.section, menu === option.section);
        const key = `fullmenu-${index}`;
        sidebarOptions.push(
          <li
            key={key}
            onClick={() => {
              clearPreviousLocation?.();
              history.push({
                pathname: option.url,
                state: { previousLocation: history.location?.pathname || 'back' },
              });
            }}
            id={`FullMenu_sideBar_${index}`}
          >
            <NavMenuItem href={option.url} tabIndex={tabIndex} title={option.title!} prefix={menuIcon} />
          </li>
        );
      });
      return sidebarOptions;
    };

    const createFullMenu = (menu: IAppMenu) => {
      const sidebarOptions: Array<ReactElement> = [];
      let menuIcon: ReactElement | string = '';

      const menuHeaders = Object.keys(menu);

      sidebarOptions.push(
        <li key={'fullmenu-fav'} onClick={changeMenu.bind(null, 'Favourites', true)} id={'FullMenu_sideBar_Favourites'}>
          <NavMenuItem
            href={'#'}
            tabIndex={tabIndex}
            onClick={(e) => {
              e.stopPropagation();
              selectMenu(1);
            }}
            title={'Favourites'}
            prefix={
              <i
                className="icon"
                style={{ fontSize: '28px', paddingLeft: '9px', paddingRight: '2px', color: '#797775' }}
              >
                star
              </i>
            }
          />
        </li>
      );
      for (let i = 0; i < menuHeaders.length; i++) {
        if (!currentUser?.systemUser?.admin && menuHeaders[i] === 'System') continue;
        menuIcon = getMenuIcon(menuHeaders[i], false);
        sidebarOptions.push(
          <li
            key={`fullmenu-${i}`}
            onClick={changeMenu.bind(null, menuHeaders[i], true)}
            id={`FullMenu_sideBar_${menuHeaders[i]}`}
          >
            <NavMenuItem
              href={`/menu/${menuHeaders[i]}`}
              tabIndex={tabIndex}
              prefix={menuIcon}
              title={menuHeaders[i]}
            />
          </li>
        );
      }

      return sidebarOptions;
    };

    const createSystemMenu = (menu) => {
      const sidebarOptions: Array<ReactElement> = [];
      let menuIcon: ReactElement | string = '';

      const menuHeaders = Object.keys(menu);

      for (let i = 0; i < menuHeaders.length; i++) {
        if (menuHeaders[i] !== 'System') continue;
        menuIcon = getMenuIcon(menuHeaders[i], menu === menuHeaders[i]);
        sidebarOptions.push(
          <li
            key={`fullmenu-${i}`}
            onClick={changeMenu.bind(null, menuHeaders[i], true)}
            id={`FullMenu_sideBar_${menuHeaders[i]}`}
          >
            <NavMenuItem
              href={'#'}
              tabIndex={tabIndex}
              className={menu === menuHeaders[i] ? 'active' : undefined}
              prefix={menuIcon}
              title={menuHeaders[i]}
            />
          </li>
        );
      }
      return sidebarOptions;
    };

    const createSetupMenu = () => {
      return <CompanySetupMenu history={history} />;
    };

    const moveOption = (ref: any, dropIndexStart: number | 'home') => {
      const screenReference = Array.isArray(ref) ? `[${ref.join(', ')}]` : ref;
      let dropIndex = dropIndexStart;
      if (
        dropIndex === 'home' ||
        (fullMenuRef.current[dropIndex].name && getIn(customMenu, ['menu', screenReference]))
      ) {
        const dropItem = getIn(customMenu, ['menu', screenReference]);
        let folderId = null;
        if (fullMenuRef.current[dropIndex]) {
          folderId = fullMenuRef.current[dropIndex].id;
        }
        menuOptions.moveItemToFolder(dispatch, dropItem.screenReference, folderId, customMenu);
        return;
      }
      dropIndex++;
      let favs = Object.assign([], favMenuRef.current); // this need to change to ref normal list :)
      const folders: { [key: string]: IFolder } = Object.keys(customMenu.folders).length > 0 ? customMenu.folders : {};
      const folderKeysArray = Object.keys(folders);
      let folderArray: Array<IFolder> = [];
      for (let k = 0; k < folderKeysArray.length; k++) {
        folderArray.push(folders[folderKeysArray[k]]);
      }

      let currentPos;
      if (getIn(customMenu, ['menu', screenReference])) {
        currentPos = getIn(customMenu, ['menu', screenReference, 'sortOrder']);
      } else {
        currentPos = getIn(customMenu, ['folders', screenReference.toString(), 'sortOrder']);
      }
      // if the drop element is moving up or down the this
      let direction = 'up';
      if (currentPos < dropIndex) {
        direction = 'down';
      }
      if (currentPos === dropIndex) {
        return;
      }

      const folderArrayInput = normalizeVariables<Array<IFolder>, Array<IFolderInput>>(folderArray);
      favs = normalizeVariables(favs, ['title', 'url', 'shortTitle', 'icon', 'exact']);
      changeSortOrder(favs, dropIndex, currentPos, direction, screenReference, folderArrayInput);

      folderListUpdate({
        variables: {
          input: {
            clientMutationId: getClientMutationId(),
            folders: folderArrayInput,
            screens: favs,
          },
        },
      }).then((_data) => {
        dispatch(menuActions.serviceBulkToMenu(_data.data?.folderListUpdate));
      });
    };

    const deleteFolder = (id: number, concurrencyVersion: number) => {
      folderUpdate({
        variables: {
          input: {
            clientMutationId: getClientMutationId(),
            folder: {
              id: id,
              concurrencyVersion: concurrencyVersion,
              deleted: true,
            },
          },
        },
      })
        .then((data) => {
          if (data.data?.folderUpdate?.result) {
            dispatch(menuActions.deleteMenuItem(data.data.folderUpdate.result));
          }
        })
        .then(() => {
          const folderArray: Array<IFolder> = [];
          const screenArray: Array<IScreen> = [];
          const tempFolders = customMenu.folders;
          const tempScreens = customMenu.menu;
          const folderHeaders = Object.keys(tempFolders);
          const screenHeaders = Object.keys(tempScreens);

          for (let i = 0; i < folderHeaders.length; i++) {
            if (tempFolders[folderHeaders[i]]?.id !== id) {
              folderArray.push(tempFolders[folderHeaders[i]]);
            }
          }

          for (let j = 0; j < screenHeaders.length; j++) {
            if (tempScreens[screenHeaders[j]].folder?.id !== id) {
              screenArray.push(tempScreens[screenHeaders[j]]);
            }
          }

          folderListUpdate({
            variables: {
              input: {
                clientMutationId: getClientMutationId(),
                folders: normalizeVariables(folderArray),
                screens: normalizeVariables(screenArray),
              },
            },
          }).then((data) => {
            dispatch(menuActions.serviceBulkToMenu(data.data?.folderListUpdate));
          });
        });
    };

    const changeSortOrder = (menu, dropIndex, currentPos, direction, ref, folder) => {
      const tempArray = menu.concat(folder);
      tempArray.sort(compare);
      for (let i = 0; i < tempArray.length; i++) {
        const folderId = tempArray[i].folder ? tempArray[i].folder.id : null;
        if (currentFolder === folderId) {
          // anything with sort order  <= to drop index needs to go down one

          if (ref !== tempArray[i].screenReference && ref !== tempArray[i].id) {
            if (direction === 'up' && tempArray[i].sortOrder >= dropIndex && tempArray[i].sortOrder <= currentPos) {
              tempArray[i].sortOrder++;
            }
            // anything with sort order  <= to drop index needs to go down one
            if (direction === 'down' && tempArray[i].sortOrder <= dropIndex && tempArray[i].sortOrder >= currentPos) {
              tempArray[i].sortOrder--;
            }
          } else {
            tempArray[i].sortOrder = dropIndex;
          }
        }
      }
    };

    const compare = (a: any, b: any) => {
      if (a.sortOrder < b.sortOrder) {
        return -1;
      }
      if (a.sortOrder > b.sortOrder) {
        return 1;
      }
      return 0;
    };

    const createFavMenu = (menu: Array<IMenuEntry> | undefined) => {
      const favMenu: Array<ReactElement> = [];
      const folderArray: Array<IFolder> = [];
      const menuFold = customMenu.folders;
      const folderKeys = Object.keys(menuFold);
      for (let j = 0; j < folderKeys.length; j++) {
        if (currentFolder === null) {
          folderArray.push(menuFold[folderKeys[j]]);
        }
      }
      fullMenuRef.current = (menu ?? []).concat(folderArray);
      fullMenuRef.current.sort(compare);
      let option;
      favMenu.push(
        <li key={`_fullMenuFav`}>
          <a
            tabIndex={tabIndex}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              selectMenu(2);
            }}
          >
            <div className="navmenu-icon">
              <i className="icon" style={{ fontSize: '28px', paddingRight: '2px' }}>
                home
              </i>
            </div>
            <div className="navmenu-title">Main Menu</div>
          </a>
        </li>
      );
      for (let i = 0; i < fullMenuRef.current.length; i++) {
        option = fullMenuRef.current[i];
        if (!option.screenReference) {
          favMenu.push(
            <DraggableMenu
              key={`key${i}`}
              menu={option}
              option={{
                fieldName: option.id,
                moveColumn: moveOption,
                folder: true,
              }}
              onFolderChange={onFolderChange}
              onBlur={onFolderBlur}
              index={i}
              editNames={editNames}
              changeMenu={setFolder}
              deleteFolder={deleteFolder}
              tabIndex={tabIndex}
            />
          );
        } else {
          favMenu.push(
            <DraggableMenu
              key={`key${i}`}
              menu={option}
              option={{
                fieldName: option.screenReference,
                moveColumn: moveOption,
              }}
              index={i}
              changeMenu={changeMenu}
              tabIndex={tabIndex}
            />
          );
        }
      }
      return favMenu;
    };

    const addFolder = () => {
      let menuCount = 1;
      const folders = Object.keys(customMenu.folders).length !== 0 ? Object.keys(customMenu.folders) : [];
      const menu = Object.keys(customMenu.menu).length !== 0 ? Object.keys(customMenu.menu) : [];
      menuCount += folders.length;
      if (menu.length) {
        for (let j = 0; j < menu.length; j++) {
          if (!customMenu.menu[menu[j]].folder) {
            menuCount++;
          }
        }
      }
      folderUpdate({
        variables: {
          input: {
            clientMutationId: getClientMutationId(),
            folder: {
              name: 'Unnamed',
              sortOrder: menuCount,
            },
          },
        },
      }).then((data) => {
        if (data.data?.folderUpdate?.result) {
          dispatch(menuActions.serviceDataToFolder(data.data.folderUpdate.result));
        }
      });
    };

    const onSearchChange: ChangeEventHandler = (value) => {
      selectMenu(0, getValue(value) || '');
    };

    const onClearSearch = () => {
      selectMenu(0, '');
    };

    const renderSubmenu = (style: CSSProperties, topMenu: ReactNode, menu: ReactNode) => {
      return (
        <div className="navmenu-options" style={style}>
          <ul className="navmenu-nav" style={{ height: menuHeightRef.current }}>
            {topMenu}
            {menu}
            <li
              key="selectMenuToggle"
              onClick={(e) => {
                e.preventDefault();
                onMenuClick(menuState);
              }}
              id="selectMenu_sideBar_toggle"
            >
              <a
                href="#"
                tabIndex={tabIndex}
                onClick={(e) => {
                  e.preventDefault();
                }}
              >
                <div className="navmenu-icon">
                  <i className="icon">swap_horiz</i>
                </div>
                <div className="navmenu-title">{menuState ? 'Hide Menu' : ''}</div>
              </a>
            </li>
          </ul>
        </div>
      );
    };

    const style: Array<React.CSSProperties> = [];
    for (let _idx = 0; _idx < 5; _idx++) {
      const offset = (menuOption - _idx) * 100;
      style[_idx] = {
        left: `${offset}%`,
        visibility: menuOption === _idx ? 'visible' : 'hidden',
      };
    }
    favMenuRef.current = menuOptions.getFavoritesMenu(customMenu, appMenu);

    const fav = createFavMenu(favMenuRef.current);
    let topOfMenu = (
      <h4 className="navmenu-heading">
        Favourites
        <span className={styles.favStyles}>
          <i className="iconSmall" onClick={currentFolder ? undefined : addFolder}>
            create_new_folder
          </i>
          <i className="iconSmall" onClick={toggleEdit}>
            mode_edit
          </i>
        </span>
      </h4>
    );
    if (currentFolder) {
      const folderTitle = getIn(customMenu, ['folders', currentFolder.toString(), 'name']);
      topOfMenu = (
        <DraggableMenu
          menu={folderTitle}
          option={{ fieldName: '', moveColumn: moveOption, folder: true }}
          index="home"
          changeMenu={setFolder}
          homeIcon={true}
        />
      );
    }

    const filteredMenu = searchValue.length > 0 ? createFilteredMenu() : null;
    const companyMenu = company && company.setUpComplete ? createFullMenu(appMenu!) : createSetupMenu();
    const systemMenu = createSystemMenu(appMenu);

    return (
      <div ref={ref} className="navmenu" onClick={focusMenuArea}>
        <div className="navmenu-container">
          {menuState ? (
            <SearchContainer>
              <SearchInput
                id={'menu-search'}
                value={searchValue}
                onChange={onSearchChange}
                placeholder={'Search menu'}
              />
              {searchValue && searchValue !== '' ? (
                <ClearSearch onClick={(e) => onClearSearch()}>
                  <i className="icon">highlight_off</i>
                </ClearSearch>
              ) : null}
            </SearchContainer>
          ) : null}
          {renderSubmenu(style[0], null, filteredMenu)}
          {renderSubmenu(style[1], topOfMenu, fav)}
          {company ? renderSubmenu(style[2], null, companyMenu) : null}
          {currentUser?.systemUser?.admin ? renderSubmenu(style[3], null, systemMenu) : null}
        </div>
      </div>
    );
  }
);

export default NavMenu;
